<template>
  <div class="amap-polygon-cover"></div>
</template>

<script lang="ts" setup>
import { ref, inject, onMounted, onUnmounted, watch, type Ref } from "vue"
import { requestAdapter } from "@p-v/api"

// 定义AMap类型
interface AMapInstance {
  add: (overlay: unknown) => void
  remove: (overlay: unknown) => void
}

interface AMapLngLatLike {
  lng: number
  lat: number
}

interface AMapPolygon {
  setPath: (path: unknown[]) => void
  setOptions: (options: Record<string, unknown>) => void
  show: () => void
  hide: () => void
}

// 定义GeoJSON相关类型
interface GeoJSONFeature {
  type: string
  geometry: {
    type: string
    coordinates: number[][][] | number[][][][]
  }
  properties?: Record<string, unknown>
}

const props = defineProps({
  // GeoJSON文件URL
  geojsonUrl: {
    type: String,
    default: "",
  },
  // 描边颜色
  strokeColor: {
    type: String,
    default: "#00eeff",
  },
  // 描边宽度
  strokeWeight: {
    type: Number,
    default: 1,
  },
  // 填充颜色
  fillColor: {
    type: String,
    default: "#71B3ff",
  },
  // 填充透明度
  fillOpacity: {
    type: Number,
    default: 1,
  },
  // 是否显示
  visible: {
    type: Boolean,
    default: true,
  },
  // 是否反转遮罩（true: 遮罩外部，false: 遮罩内部）
  maskOutside: {
    type: Boolean,
    default: true,
  },
})

const emits = defineEmits(["update:visible"])

// 从父组件注入高德地图实例
const amap = inject<Ref<AMapInstance | null>>("amap", ref(null))
const polygon = ref<AMapPolygon | null>(null)
const geojsonBoundaries = ref<number[][][]>([])
const isLoading = ref(false)

// 从GeoJSON URL加载数据
const fetchGeoJSON = async () => {
  if (!props.geojsonUrl) return

  try {
    isLoading.value = true
    const response = await requestAdapter<{
      type: string
      features: GeoJSONFeature[]
    }>({
      url: props.geojsonUrl,
      method: "GET",
    })

    if (response && response.features) {
      // 处理GeoJSON数据
      processGeoJSONFeatures(response.features)
    }
  } catch (error) {
    console.error("加载GeoJSON数据失败", error)
  } finally {
    isLoading.value = false
  }
}

// 处理GeoJSON特性
const processGeoJSONFeatures = (features: GeoJSONFeature[]) => {
  const boundaries: number[][][] = []

  features.forEach(feature => {
    if (feature.geometry.type === "Polygon") {
      // Polygon类型直接添加坐标
      boundaries.push(...(feature.geometry.coordinates as number[][][]))
    } else if (feature.geometry.type === "MultiPolygon") {
      // MultiPolygon类型需要展平一层
      const multiPolygon = feature.geometry.coordinates as number[][][][]
      multiPolygon.forEach(polygonCoords => {
        boundaries.push(...polygonCoords)
      })
    }
  })

  // 更新边界数据
  geojsonBoundaries.value = boundaries
  // 创建多边形遮罩
  createPolygonCover()
}

// 创建多边形遮罩
const createPolygonCover = () => {
  if (!amap.value || !geojsonBoundaries.value.length) return

  // 清除之前的多边形
  if (polygon.value) {
    amap.value.remove(polygon.value)
    polygon.value = null
  }

  try {
    // 如果提供了自定义路径，则直接使用
    let pathArray: unknown[] = []
    const outer = [
      new window.AMap.LngLat(-360, 90, true),
      new window.AMap.LngLat(-360, -90, true),
      new window.AMap.LngLat(360, -90, true),
      new window.AMap.LngLat(360, 90, true),
    ]

    // 合并props.boundaries和geojsonBoundaries
    const allBoundaries: any[] = []
    if (geojsonBoundaries.value.length > 0) {
      allBoundaries.push(...geojsonBoundaries.value)
    }

    // 使用提供的boundaries作为洞
    const holes = allBoundaries.map(boundary => boundary.map(point => new window.AMap.LngLat(point[0], point[1], true)))

    // 创建路径数组
    pathArray = [outer]
    if (props.maskOutside) {
      // 如果是遮罩外部，则将boundaries作为洞
      pathArray.push(...holes)
    } else {
      // 如果是遮罩内部，则替换路径数组
      pathArray = [...holes]
    }

    // 创建多边形
    polygon.value = new window.AMap.Polygon({
      path: pathArray,
      strokeColor: props.strokeColor,
      strokeWeight: props.strokeWeight,
      fillColor: props.fillColor,
      fillOpacity: props.fillOpacity,
    }) as AMapPolygon

    // 将多边形添加到地图
    if (props.visible) {
      amap.value.add(polygon.value)
    }
  } catch (error) {
    console.error("创建多边形遮罩失败", error)
  }
}

// 更新多边形可见性
const updateVisibility = (visible: boolean) => {
  if (!polygon.value) return

  if (visible) {
    polygon.value.show()
  } else {
    polygon.value.hide()
  }

  emits("update:visible", visible)
}

// 监听GeoJSON URL变化
watch(
  () => props.geojsonUrl,
  newUrl => {
    if (newUrl) {
      fetchGeoJSON()
    } else {
      // 清空GeoJSON边界数据
      geojsonBoundaries.value = []
      createPolygonCover()
    }
  },
)

// 监听可见性变化
watch(
  () => props.visible,
  newVal => {
    updateVisibility(newVal)
  },
)

// 监听样式属性变化
watch([() => props.strokeColor, () => props.strokeWeight, () => props.fillColor, () => props.fillOpacity], () => {
  if (!polygon.value) return

  polygon.value.setOptions({
    strokeColor: props.strokeColor,
    strokeWeight: props.strokeWeight,
    fillColor: props.fillColor,
    fillOpacity: props.fillOpacity,
  })
})

// 监听遮罩类型变化
watch(
  () => props.maskOutside,
  () => {
    createPolygonCover()
  },
)

// 组件挂载时创建多边形
onMounted(() => {
  // 如果提供了GeoJSON URL，先获取GeoJSON数据
  if (props.geojsonUrl) {
    fetchGeoJSON()
  }

  if (amap.value) {
    createPolygonCover()
  } else {
    // 当地图实例变为可用时创建多边形
    watch(
      () => amap.value,
      newVal => {
        if (newVal) {
          createPolygonCover()
        }
      },
      { immediate: true },
    )
  }
})

// 组件卸载前清除多边形
onUnmounted(() => {
  if (amap.value && polygon.value) {
    amap.value.remove(polygon.value)
    polygon.value = null
  }
})

// 对外暴露方法
defineExpose({
  /**
   * 设置多边形边界
   * @param boundaries 多边形边界坐标集合
   */
  setBoundaries(boundaries: number[][][]) {
    if (polygon.value && boundaries) {
      const holes = boundaries.map(boundary => boundary.map(point => new window.AMap.LngLat(point[0], point[1], true)))

      const outer = [
        new window.AMap.LngLat(-360, 90, true),
        new window.AMap.LngLat(-360, -90, true),
        new window.AMap.LngLat(360, -90, true),
        new window.AMap.LngLat(360, 90, true),
      ]

      let pathArray = [outer]
      if (props.maskOutside) {
        pathArray.push(...holes)
      } else {
        pathArray = [...holes]
      }

      polygon.value.setPath(pathArray)
    }
  },

  /**
   * 设置自定义路径
   * @param paths 自定义路径数组
   */
  setCustomPaths(paths: unknown[]) {
    if (polygon.value && paths) {
      polygon.value.setPath(paths)
    }
  },

  /**
   * 显示多边形
   */
  show() {
    updateVisibility(true)
  },

  /**
   * 隐藏多边形
   */
  hide() {
    updateVisibility(false)
  },

  /**
   * 获取多边形实例
   * @returns 高德地图多边形实例
   */
  getPolygon() {
    return polygon.value
  },

  /**
   * 设置GeoJSON URL并加载数据
   * @param url GeoJSON数据的URL地址
   */
  setGeoJsonUrl(url: string) {
    if (url) {
      // 手动更新URL并获取数据
      geojsonBoundaries.value = []
      fetchGeoJSON()
    }
  },

  /**
   * 获取当前加载状态
   * @returns 是否正在加载数据
   */
  isLoading() {
    return isLoading.value
  },
})
</script>

<style lang="scss">
.amap-polygon-cover {
  display: none;
}
</style>
